(function () {
  'use strict';

  var VERSION = '2026-09-28-v7';
  var shopifyCatalogPromise = null;

  function number(value) {
    var parsed = Number(value);
    return isFinite(parsed) ? parsed : 0;
  }

  function normalize(value) {
    return String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
  }

  function score(review) {
    return Math.max(0, Math.min(5, number(review && review.score)));
  }

  function timestamp(review) {
    var parsed = Date.parse(review && review.created_at ? review.created_at : 0);
    return isNaN(parsed) ? 0 : parsed;
  }

  function reviewCopy(review) {
    return normalize(review && (review.content || review.title));
  }

  function reviewerName(review) {
    return normalize(review && review.user && review.user.display_name) || 'Nest Bedding Customer';
  }

  function productTitle(value) {
    if (typeof value === 'string') return normalize(value);
    return normalize(value && (value.name || value.title || value.product_title || value.product_name));
  }

  function reviewProductKeys(review) {
    var product = review && review.product && typeof review.product === 'object' ? review.product : {};
    return [
      review && review._landing_product_id,
      review && review.domain_key,
      review && review.sku,
      review && review.product_id,
      review && review.external_product_id,
      product.domain_key,
      product.id,
      product.external_product_id,
      product.sku,
      product.handle
    ].map(normalize).filter(Boolean);
  }

  function addCatalogItem(catalog, item, fallbackKey) {
    var title = productTitle(item);
    if (!title) return;

    [
      fallbackKey,
      item && item.id,
      item && item.product_id,
      item && item.domain_key,
      item && item.external_product_id,
      item && item.sku,
      item && item.handle
    ].map(normalize).filter(Boolean).forEach(function (key) {
      catalog[key] = title;
    });
  }

  function productCatalog(data) {
    var catalog = {};
    [data && data.products, data && data.product_catalog, data && data.products_data].forEach(function (source) {
      if (Array.isArray(source)) {
        source.forEach(function (item) { addCatalogItem(catalog, item, ''); });
      } else if (source && typeof source === 'object') {
        Object.keys(source).forEach(function (key) {
          var item = source[key];
          if (typeof item === 'string') catalog[normalize(key)] = normalize(item);
          else addCatalogItem(catalog, item, key);
        });
      }
    });
    return catalog;
  }

  function productName(review, catalog) {
    var product = review && review.product && typeof review.product === 'object' ? review.product : {};
    var direct = productTitle(
      (review && review._landing_product_title) ||
      (review && (review.product_title || review.product_name || review.product_data)) ||
      (review && review.product) ||
      product
    );
    if (direct) return direct;

    var keys = reviewProductKeys(review);
    for (var index = 0; index < keys.length; index += 1) {
      if (catalog && catalog[keys[index]]) return catalog[keys[index]];
    }
    return 'Nest Bedding product';
  }

  function loadShopifyCatalog() {
    if (shopifyCatalogPromise) return shopifyCatalogPromise;
    shopifyCatalogPromise = fetch('/products.json?limit=250', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Shopify product catalog request failed');
        return response.json();
      })
      .then(function (payload) {
        var catalog = { titles: {}, mattressKeys: {} };
        (payload && Array.isArray(payload.products) ? payload.products : []).forEach(function (item) {
          addCatalogItem(catalog.titles, item, '');
          if (normalize(item && item.product_type).toLowerCase().indexOf('mattress') === -1) return;
          [item.id, item.handle].map(normalize).filter(Boolean).forEach(function (key) {
            catalog.mattressKeys[key] = true;
          });
          (item.variants || []).forEach(function (variant) {
            [variant.id, variant.sku].map(normalize).filter(Boolean).forEach(function (key) {
              catalog.mattressKeys[key] = true;
            });
          });
        });
        return catalog;
      })
      .catch(function () { return { titles: {}, mattressKeys: {} }; });
    return shopifyCatalogPromise;
  }

  function displayDate(value) {
    var date = new Date(value || 0);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  function setStars(group, rating) {
    if (!group) return;
    var bounded = Math.max(0, Math.min(5, number(rating)));
    Array.prototype.forEach.call(group.querySelectorAll('[data-review-star]'), function (star, index) {
      var fill = Math.max(0, Math.min(1, bounded - index)) * 100;
      star.style.setProperty('--dynamic-star-fill', fill + '%');
    });
    var ratingLabel = group.parentNode && group.parentNode.querySelector('[data-card-rating]');
    if (ratingLabel) ratingLabel.textContent = 'Rated ' + bounded.toFixed(1) + ' out of 5 stars';
  }

  function payloadData(payload) {
    return payload && payload.response ? payload.response : (payload || {});
  }

  function payloadReviews(data) {
    if (Array.isArray(data.reviews)) return data.reviews;
    if (Array.isArray(data.results)) return data.results;
    return [];
  }

  function isSiteReview(review) {
    var product = review && review.product ? review.product : {};
    var key = normalize(
      (review && (review.domain_key || review.sku || review.product_id)) ||
      product.domain_key ||
      product.id ||
      ''
    );
    return key === 'yotpo_site_reviews';
  }

  function averageRating(data, reviews) {
    var bottomline = data.bottomline || {};
    var average = bottomline.average_score;

    if (average == null && reviews.length) {
      average = reviews.reduce(function (sum, review) {
        return sum + score(review);
      }, 0) / reviews.length;
    }

    return Math.max(0, Math.min(5, number(average)));
  }

  function isMattressReview(review, shopifyCatalog) {
    var type = normalize(
      (review && (review.product_type || review.product_category)) ||
      (review && review.product && review.product.product_type)
    ).toLowerCase();
    if (type.indexOf('mattress') !== -1) return true;

    var directTitle = productName(review, {});
    if (directTitle.toLowerCase().indexOf('mattress') !== -1) return true;

    var mattressKeys = shopifyCatalog && shopifyCatalog.mattressKeys ? shopifyCatalog.mattressKeys : {};
    return reviewProductKeys(review).some(function (key) { return mattressKeys[key] === true; });
  }

  function selectedReviews(reviews, count, shopifyCatalog, requestedIds) {
    var requested = {};
    (requestedIds || []).forEach(function (id) { requested[normalize(id)] = true; });
    var requestedMode = Object.keys(requested).length > 0;
    var eligible = reviews
      .filter(function (review) {
        var verified = review.verified_buyer === true || review.verified_buyer === 'true';
        var matchesRequested = requestedMode
          ? reviewProductKeys(review).some(function (key) { return requested[key] === true; })
          : isMattressReview(review, shopifyCatalog);
        return verified && score(review) > 0 && reviewCopy(review) && matchesRequested;
      })
      .sort(function (a, b) {
        if (requestedMode && score(b) !== score(a)) return score(b) - score(a);
        return timestamp(b) - timestamp(a);
      });

    if (!requestedMode) return eligible.slice(0, count);

    var unique = [];
    var seenIds = {};
    var seenCopy = {};
    eligible.forEach(function (review) {
      var idKey = normalize(review && review.id);
      var copyKey = (reviewerName(review) + '|' + reviewCopy(review)).toLowerCase();
      if ((idKey && seenIds[idKey]) || seenCopy[copyKey]) return;
      if (idKey) seenIds[idKey] = true;
      seenCopy[copyKey] = true;
      unique.push(review);
    });

    var selected = [];
    var selectedIds = {};
    var representedProducts = {};
    unique.forEach(function (review) {
      var productId = normalize(review && review._landing_product_id);
      if (selected.length >= count || representedProducts[productId]) return;
      selected.push(review);
      selectedIds[normalize(review.id) || (reviewerName(review) + '|' + reviewCopy(review))] = true;
      representedProducts[productId] = true;
    });
    unique.forEach(function (review) {
      var selectionKey = normalize(review.id) || (reviewerName(review) + '|' + reviewCopy(review));
      if (selected.length >= count || selectedIds[selectionKey]) return;
      selected.push(review);
      selectedIds[selectionKey] = true;
    });
    return selected.slice(0, count);
  }

  function updateAggregate(root, rating) {
    var aggregate = root.querySelector('[data-review-aggregate]');
    if (!aggregate || !rating) return;

    var average = aggregate.querySelector('[data-review-average]');
    if (average) average.textContent = rating.toFixed(1);
    setStars(aggregate.querySelector('[data-review-aggregate-stars]'), rating);
    aggregate.classList.add('is-ready');
    aggregate.setAttribute('aria-hidden', 'false');
  }

  function updateCard(card, review, catalog) {
    var copy = card.querySelector('[data-review-copy]');
    var author = card.querySelector('[data-review-author]');
    var meta = card.querySelector('[data-review-meta]');
    var product = card.querySelector('[data-review-product]');
    var verified = review.verified_buyer === true || review.verified_buyer === 'true';
    var date = displayDate(review.created_at);
    var details = [];

    if (copy) {
      copy.textContent = reviewCopy(review);
      copy.setAttribute('title', reviewCopy(review));
    }
    if (author) author.textContent = reviewerName(review);
    if (product) product.textContent = productName(review, catalog);
    if (verified) details.push('Verified Buyer');
    if (date) details.push(date);
    if (meta) meta.textContent = details.join(' · ');

    setStars(card.querySelector('[data-card-stars]'), score(review));
    card.setAttribute('data-review-id', normalize(review.id));
  }

  function requestedProductIds(root) {
    return normalize(root.getAttribute('data-product-review-ids'))
      .split(',')
      .map(normalize)
      .filter(Boolean);
  }

  function requestedProductLabels(root) {
    var labels = {};
    normalize(root.getAttribute('data-product-review-labels'))
      .split('||')
      .forEach(function (entry) {
        var parts = entry.split('::');
        var id = normalize(parts.shift());
        var title = normalize(parts.join('::'));
        if (id && title) labels[id] = title;
      });
    return labels;
  }

  function fetchRequestedProductReviews(root, productIds) {
    var appKey = normalize(root.getAttribute('data-yotpo-app-key'));
    var labels = requestedProductLabels(root);
    if (!appKey || !productIds.length) return null;

    return Promise.all(productIds.map(function (productId) {
      var url = 'https://api-cdn.yotpo.com/v1/widget/' + encodeURIComponent(appKey) +
        '/products/' + encodeURIComponent(productId) + '/reviews.json?per_page=10&page=1';
      return fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) throw new Error('Product reviews request failed');
          return response.json();
        })
        .then(function (payload) {
          return { productId: productId, payload: payload };
        });
    })).then(function (entries) {
      var combined = { response: { reviews: [], products: {} } };
      entries.forEach(function (entry) {
        var data = payloadData(entry.payload);
        combined.response.products[entry.productId] = {
          id: entry.productId,
          title: labels[entry.productId] || ''
        };
        payloadReviews(data).forEach(function (review) {
          review._landing_product_id = entry.productId;
          review._landing_product_title = labels[entry.productId] || 'Nest Bedding pillow';
          combined.response.reviews.push(review);
        });
      });
      return combined;
    });
  }

  function init(root) {
    if (!root || root.getAttribute('data-dynamic-testimonials-version') === VERSION) return;
    root.setAttribute('data-dynamic-testimonials-version', VERSION);

    var productEndpoint = root.getAttribute('data-product-review-endpoint');
    var siteRatingEndpoint = root.getAttribute('data-site-rating-endpoint');
    var productIds = requestedProductIds(root);
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-dynamic-review-card]'));
    if ((!productEndpoint && !productIds.length) || !siteRatingEndpoint || !cards.length || !window.fetch) return;

    var requestedReviewFetch = fetchRequestedProductReviews(root, productIds);
    var reviewFetch = requestedReviewFetch || fetch(productEndpoint, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ per_page: 30 })
    }).then(function (response) {
      if (!response.ok) throw new Error('Product reviews request failed');
      return response.json();
    });

    reviewFetch
      .then(function (payload) {
        return Promise.all([payload, loadShopifyCatalog()]);
      })
      .then(function (results) {
        var payload = results[0];
        var shopifyCatalog = results[1];
        var data = payloadData(payload);
        var reviews = payloadReviews(data).filter(function (review) {
          return !isSiteReview(review);
        });
        var selected = selectedReviews(reviews, cards.length, shopifyCatalog, productIds);
        var catalog = Object.assign({}, productCatalog(data), shopifyCatalog.titles);
        if (!selected.length) throw new Error('No eligible product reviews returned');

        cards.forEach(function (card, index) {
          if (selected[index]) {
            updateCard(card, selected[index], catalog);
            card.removeAttribute('hidden');
          } else {
            card.setAttribute('hidden', '');
          }
        });

        root.classList.add('dynamic-testimonials--loaded');
      })
      .catch(function () {
        root.classList.add('dynamic-testimonials--fallback');
      });

    fetch(siteRatingEndpoint, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Site rating request failed');
        return response.json();
      })
      .then(function (payload) {
        var data = payloadData(payload);
        var rating = averageRating(data, payloadReviews(data));
        updateAggregate(root, rating);
        Array.prototype.forEach.call(document.querySelectorAll('[data-landing-rating-strip]'), function (strip) {
          updateAggregate(strip, rating);
        });
      })
      .catch(function () {
        root.classList.add('dynamic-testimonials--rating-unavailable');
      });
  }

  function initAll(scope) {
    var root = scope && scope.matches && scope.matches('[data-dynamic-testimonials]')
      ? scope
      : null;
    if (root) init(root);

    Array.prototype.forEach.call(
      (scope || document).querySelectorAll('[data-dynamic-testimonials]'),
      init
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    }, { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
